import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { apiService, type TwoFactorStatus } from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import { TwoFactorActiveNotice } from './TwoFactorActiveNotice';
import { TwoFactorSetupPanel } from './TwoFactorSetupPanel';

interface TwoFactorDialogProps {
  /** Steuert die Sichtbarkeit des Dialogs. */
  open: boolean;
  /** Wird bei jedem Öffnen/Schließen (z.B. ESC, Klick auf Overlay) aufgerufen. */
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog zur Verwaltung der eigenen Zwei-Faktor-Authentifizierung: zeigt den Status,
 * führt bei inaktiver 2FA durch die Einrichtung und informiert bei aktiver 2FA über
 * den Aktivierungszeitpunkt. Deaktivieren ist bewusst nicht möglich (nur Reset durch Super Admin).
 */
export function TwoFactorDialog({ open, onOpenChange }: TwoFactorDialogProps) {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [loadError, setLoadError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  // Status bei jedem Öffnen frisch laden; "reloadKey" erlaubt Neuladen nach Aktivierung/Fehler.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadError('');
    apiService
      .get2FAStatus()
      .then((result) => {
        if (!cancelled) setStatus(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(extractErrorMessage(err, 'Status konnte nicht geladen werden.'));
      });
    return () => {
      cancelled = true;
    };
  }, [open, reloadKey]);

  // Beim Schließen verwerfen, damit beim nächsten Öffnen kein veralteter Status aufblitzt.
  useEffect(() => {
    if (!open) setStatus(null);
  }, [open]);

  const renderBody = () => {
    if (loadError) {
      return (
        <div className="space-y-3">
          <p className="text-sm text-red-700">{loadError}</p>
          <Button variant="outline" onClick={() => setReloadKey((key) => key + 1)}>
            Erneut versuchen
          </Button>
        </div>
      );
    }
    if (!status) {
      return (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" aria-label="Lädt" />
        </div>
      );
    }
    if (status.enabled) {
      return <TwoFactorActiveNotice enabledAt={status.enabledAt} />;
    }
    return <TwoFactorSetupPanel onActivated={() => setReloadKey((key) => key + 1)} />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Zwei-Faktor-Authentifizierung</DialogTitle>
          <DialogDescription>Zusätzlicher Schutz für deine Anmeldung im Admin-Portal.</DialogDescription>
        </DialogHeader>
        {renderBody()}
      </DialogContent>
    </Dialog>
  );
}
